import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import "./Tooltip.css";

const VIEWPORT_PADDING = 8;

const Tooltip = ({ text, children, delay = 300 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const timeoutRef = useRef(null);
  const wrapperRef = useRef(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const updatePosition = () => {
    if (!wrapperRef.current) return;

    const rect = wrapperRef.current.getBoundingClientRect();
    const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
    const scrollY = window.pageYOffset || document.documentElement.scrollTop;

    setPosition({
      top: rect.bottom + scrollY + 2,
      left: rect.left + scrollX + rect.width / 2,
    });
  };

  const handleMouseEnter = () => {
    if (!text) return;
    updatePosition();
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, delay);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(false);
  };

  useLayoutEffect(() => {
    if (!isVisible || !tooltipRef.current) return;

    const tooltipEl = tooltipRef.current;
    const tooltipRect = tooltipEl.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    const leftEdge = tooltipRect.left;
    const rightEdge = tooltipRect.right;

    let offsetX = 0;
    if (leftEdge < VIEWPORT_PADDING) {
      offsetX = VIEWPORT_PADDING - leftEdge;
    } else if (rightEdge > viewportWidth - VIEWPORT_PADDING) {
      offsetX = viewportWidth - VIEWPORT_PADDING - rightEdge;
    }

    if (offsetX !== 0) {
      tooltipEl.style.marginLeft = `${offsetX}px`;
    } else {
      tooltipEl.style.marginLeft = "";
    }
  }, [isVisible, position]);

  if (!text) {
    return <>{children}</>;
  }

  return (
    <>
      <div
        className="tooltip-wrapper"
        ref={wrapperRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>
      {isVisible &&
        createPortal(
          <div
            ref={tooltipRef}
            className="tooltip"
            style={{
              top: `${position.top}px`,
              left: `${position.left}px`,
              transform: "translateX(-50%)",
            }}
          >
            {text}
            <div className="tooltip-arrow" />
          </div>,
          document.body
        )}
    </>
  );
};

export default Tooltip;
